import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Check, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import musikonzeptLogo from "@/assets/musikonzept-logo.png";
import { safeGetItem, safeRemoveItem } from "@/lib/safeStorage";
import { passwortFehlerText } from "@/lib/passwortFehler";
import { passwortMailFehler } from "@/lib/passwortVergessen";
import { z } from "zod";
import { Helmet } from "react-helmet-async";

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Bitte gib eine gültige E-Mail-Adresse ein" }),
  password: z.string().min(1, { message: "Bitte gib dein Passwort ein" }),
});

const passwordSchema = z.object({
  password: z.string()
    .min(8, { message: "Mindestens 8 Zeichen" })
    .regex(/[a-z]/, { message: "Mindestens ein Kleinbuchstabe" })
    .regex(/[A-Z]/, { message: "Mindestens ein Großbuchstabe" })
    .regex(/[0-9]/, { message: "Mindestens eine Zahl" }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwörter stimmen nicht überein",
  path: ["confirmPassword"],
});

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  // Password setup state
  const [isPasswordRecoveryMode, setIsPasswordRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  
  // Forgot password state
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [isSendingResetEmail, setIsSendingResetEmail] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Helper function to check if URL contains recovery indicators
  const checkUrlForRecovery = () => {
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);
    const hasRecoveryInHash = hash.includes('type=recovery');
    const hasRecoveryInSearch = searchParams.get('type') === 'recovery';
    return hasRecoveryInHash || hasRecoveryInSearch;
  };

  // Track if we're in recovery flow (persists across re-renders)
  const [isRecoveryFlowActive, setIsRecoveryFlowActive] = useState(checkUrlForRecovery);

  useEffect(() => {
    const checkAuthAndRedirect = async (userId: string) => {
      // Check for pending free course enrollment
      const pendingCourseId = safeGetItem('pending_free_course_id');
      const pendingCourseSlug = safeGetItem('pending_free_course_slug');
      
      if (pendingCourseId && pendingCourseSlug) {
        try {
          const { data, error } = await supabase.functions.invoke("free-course-enroll", {
            body: { course_id: pendingCourseId }
          });
          
          // Clean up localStorage
          safeRemoveItem('pending_free_course_id');
          safeRemoveItem('pending_free_course_slug');
          
          if (!error && !data?.error) {
            // Ensure access + enrollments update immediately (avoid needing a manual refresh)
            await queryClient.invalidateQueries({ queryKey: ["global-access"] });
            await queryClient.invalidateQueries({ queryKey: ["course-enrollment"] });
            await queryClient.invalidateQueries({ queryKey: ["course-enrollment-check"] });
            await queryClient.invalidateQueries({ queryKey: ["student-courses"] });
            await queryClient.invalidateQueries({ queryKey: ["dashboard-courses"] });
            await queryClient.refetchQueries({ queryKey: ["global-access"] });

            toast.success("Erfolgreich eingeschrieben!", {
              description: "Du kannst jetzt mit dem Kurs starten.",
            });
            navigate(`/mein-bereich/kurs/${pendingCourseSlug}`);
            return;
          }
        } catch (e) {
          console.error('Auto-enrollment failed:', e);
          // Clean up on error too
          safeRemoveItem('pending_free_course_id');
          safeRemoveItem('pending_free_course_slug');
        }
      }
      
      // Check redirect parameter
      const searchParams = new URLSearchParams(window.location.search);
      const redirectTo = searchParams.get('redirect');
      
      if (redirectTo && redirectTo.startsWith('/')) {
        navigate(redirectTo);
        return;
      }
      
      // Check if user has admin or super_admin role
      const { data: hasAdmin } = await supabase
        .rpc('has_role', { _user_id: userId, _role: 'admin' });
      const { data: hasSuperAdmin } = await supabase
        .rpc('has_role', { _user_id: userId, _role: 'super_admin' });
      
      if (hasAdmin || hasSuperAdmin) {
        navigate('/admin/dashboard');
      } else {
        navigate('/mein-bereich');
      }
    };

    const checkAuth = async () => {
      // Check URL again in case state was stale
      const urlHasRecovery = checkUrlForRecovery();
      
      // If recovery flow is active, don't redirect - show password form instead
      if (isRecoveryFlowActive || urlHasRecovery) {
        console.log("[Login] Recovery flow active, skipping auth check redirect");
        setIsRecoveryFlowActive(true);
        setIsPasswordRecoveryMode(true);
        setIsCheckingAuth(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        await checkAuthAndRedirect(session.user.id);
      }
      setIsCheckingAuth(false);
    };

    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Always check URL directly to avoid stale closure issues
      const urlHasRecovery = checkUrlForRecovery();
      
      console.log("[Login] Auth event:", event, "Recovery active:", isRecoveryFlowActive, "URL has recovery:", urlHasRecovery);
      
      if (event === 'PASSWORD_RECOVERY') {
        // User clicked the password recovery link - show password setup form
        console.log("[Login] PASSWORD_RECOVERY event detected");
        setIsRecoveryFlowActive(true);
        setIsPasswordRecoveryMode(true);
        setIsCheckingAuth(false);
        return;
      }
      
      // Only redirect if NOT in recovery flow - check both state AND URL
      if (event === 'SIGNED_IN' && session && !isRecoveryFlowActive && !isPasswordRecoveryMode && !urlHasRecovery) {
        // Wait for session to be fully persisted before navigating.
        // This prevents a white-screen race condition where the target page
        // tries to read the session before it's available in storage.
        setTimeout(async () => {
          // Verify session is readable before redirecting
          const { data: { session: verifiedSession } } = await supabase.auth.getSession();
          if (verifiedSession) {
            checkAuthAndRedirect(verifiedSession.user.id);
          }
        }, 300);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isPasswordRecoveryMode, isRecoveryFlowActive, queryClient]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate input
    const validation = loginSchema.safeParse({ email, password });
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    setIsLoading(true);
    setLoginError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          setLoginError("no_account_or_wrong_password");
        } else if (error.message.includes("Email not confirmed")) {
          toast.error("E-Mail nicht bestätigt", {
            description: "Bitte bestätige zuerst deine E-Mail-Adresse.",
          });
        } else {
          toast.error("Anmeldung fehlgeschlagen", {
            description: error.message,
          });
        }
        return;
      }

      if (data.user) {
        toast.success("Erfolgreich angemeldet!");
        // Redirect will be handled by onAuthStateChange
      }
    } catch (error: any) {
      toast.error("Ein Fehler ist aufgetreten", {
        description: "Bitte versuche es später erneut.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords
    const validation = passwordSchema.safeParse({ password: newPassword, confirmPassword });
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    setIsSettingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        toast.error("Fehler beim Speichern", {
          description: passwortFehlerText(error, error.message),
        });
        return;
      }

      toast.success("Passwort erfolgreich gespeichert!", {
        description: "Du wirst jetzt zu deinem Bereich weitergeleitet.",
      });

      // Clear the recovery mode and redirect
      setIsPasswordRecoveryMode(false);
      
      // Small delay before redirect
      setTimeout(() => {
        navigate('/mein-bereich');
      }, 1000);
    } catch (error: any) {
      toast.error("Ein Fehler ist aufgetreten", {
        description: "Bitte versuche es später erneut.",
      });
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Bitte gib deine E-Mail-Adresse ein");
      return;
    }

    const emailValidation = z.string().email().safeParse(email.trim());
    if (!emailValidation.success) {
      toast.error("Bitte gib eine gültige E-Mail-Adresse ein");
      return;
    }

    setIsSendingResetEmail(true);

    try {
      // Use custom edge function instead of Supabase built-in to get MusiKonzept branding
      const { data, error: fnError } = await supabase.functions.invoke('send-password-setup-email', {
        body: {
          email: email.trim(),
          redirect_to: `${window.location.origin}/login`,
          template_key: 'password_reset',
        },
      });

      if (fnError) {
        const hinweis = await passwortMailFehler(fnError);
        toast.error(hinweis.titel, { description: hinweis.text });
        return;
      }

      if (data?.skipped) {
        toast.error("E-Mail konnte nicht gesendet werden", {
          description: "Bitte wende dich an den Support.",
        });
        return;
      }

      toast.success("E-Mail gesendet!", {
        description: "Schau in deinem Posteingang nach, auch im Spam-Ordner.",
      });
      
      // Go back to login mode
      setIsForgotPasswordMode(false);
    } catch (error: any) {
      toast.error("Ein Fehler ist aufgetreten", {
        description: "Bitte versuche es später erneut.",
      });
    } finally {
      setIsSendingResetEmail(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Password setup mode (Express Sign-In)
  if (isPasswordRecoveryMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center">
              <img 
                src={musikonzeptLogo} 
                alt="MusiKonzept" 
                className="h-16 w-auto"
              />
            </Link>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-bold">Passwort festlegen</CardTitle>
              <CardDescription>
                Lege dein persönliches Passwort fest, um auf deine Kurse zugreifen zu können.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Neues Passwort</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      disabled={isSettingPassword}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  
                  {/* Password requirements */}
                  <div className="text-xs space-y-1 mt-2">
                    <div className={`flex items-center gap-1 ${newPassword.length >= 8 ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {newPassword.length >= 8 ? <Check className="h-3 w-3" /> : <span className="w-3">•</span>}
                      Mindestens 8 Zeichen
                    </div>
                    <div className={`flex items-center gap-1 ${/[a-z]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {/[a-z]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3">•</span>}
                      Mindestens ein Kleinbuchstabe
                    </div>
                    <div className={`flex items-center gap-1 ${/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {/[A-Z]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3">•</span>}
                      Mindestens ein Großbuchstabe
                    </div>
                    <div className={`flex items-center gap-1 ${/[0-9]/.test(newPassword) ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {/[0-9]/.test(newPassword) ? <Check className="h-3 w-3" /> : <span className="w-3">•</span>}
                      Mindestens eine Zahl
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    disabled={isSettingPassword}
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-destructive">Passwörter stimmen nicht überein</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isSettingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                  size="lg"
                >
                  {isSettingPassword ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    "Passwort speichern"
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col gap-4 text-center text-sm">
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
                ← Zurück zur Startseite
              </Link>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Forgot password mode
  if (isForgotPasswordMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center">
              <img 
                src={musikonzeptLogo} 
                alt="MusiKonzept" 
                className="h-16 w-auto"
              />
            </Link>
          </div>

          <Card className="border-border/50 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-bold">Passwort vergessen?</CardTitle>
              <CardDescription>
                Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen deines Passworts.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">E-Mail-Adresse</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="deine@email.de"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    disabled={isSendingResetEmail}
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isSendingResetEmail || !email.trim()}
                  size="lg"
                >
                  {isSendingResetEmail ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    "Reset-Link senden"
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col gap-4 text-center text-sm">
              <button
                type="button"
                onClick={() => setIsForgotPasswordMode(false)}
                className="text-primary font-medium hover:underline"
              >
                ← Zurück zur Anmeldung
              </button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Normal login mode
  return (
    <>
      <Helmet>
        <title>MusiKonzept Login – Mitgliederbereich der Musik-Akademie</title>
        <meta
          name="description"
          content="Login zum MusiKonzept-Mitgliederbereich. Greife auf deine Online-Kurse, Webinare und Materialien für musikalische Früherziehung zu."
        />
        <link rel="canonical" href="https://www.musikonzept.de/login" />
        <meta property="og:title" content="MusiKonzept Login – Mitgliederbereich" />
        <meta
          property="og:description"
          content="Melde dich im MusiKonzept-Mitgliederbereich an und starte deine musikpädagogische Weiterbildung."
        />
        <meta property="og:url" content="https://www.musikonzept.de/login" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
        <h1 className="sr-only">MusiKonzept Login – Mitgliederbereich der Online-Akademie für Musikpädagogik</h1>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center">
            <img 
              src={musikonzeptLogo} 
              alt="MusiKonzept" 
              className="h-16 w-auto"
            />
          </Link>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-bold">Willkommen zurück!</CardTitle>
            <CardDescription>
              Melde dich an, um auf deine Kurse zuzugreifen
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loginError === "no_account_or_wrong_password" && (
              <Alert variant="destructive" className="text-left">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <p className="font-medium">E-Mail oder Passwort ist falsch.</p>
                  <p className="text-sm">
                    Du hast noch kein Konto? Falls du auf unserer alten Lernplattform warst, musst du dich hier neu registrieren – deine Kurse und Zertifikate werden automatisch übernommen.
                  </p>
                  <Link 
                    to={`/registrieren${location.search}`} 
                    className="inline-block text-sm font-medium underline hover:no-underline mt-1"
                  >
                    → Jetzt kostenlos registrieren
                  </Link>
                </AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="deine@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Passwort</Label>
                  <button
                    type="button"
                    onClick={() => setIsForgotPasswordMode(true)}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    Passwort vergessen?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    disabled={isLoading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Anmelden...
                  </>
                ) : (
                  "Anmelden"
                )}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 text-center text-sm">
            {/* Migration hint - always visible */}
            <div className="rounded-lg border border-border bg-muted/50 px-4 py-3 text-xs text-muted-foreground leading-relaxed">
              <p>
                <span className="font-medium text-foreground">Login klappt nicht?</span>{" "}
                Nach dem Umzug auf unsere neue Plattform ist eine einmalige Neu-Registrierung nötig – auch wenn du bereits ein Konto hattest. Deine Kurse und Zertifikate werden automatisch übernommen.{" "}
                <Link to={`/registrieren${location.search}`} className="text-primary font-medium hover:underline">
                  Jetzt registrieren →
                </Link>
              </p>
            </div>
            <p className="text-muted-foreground">
              Noch kein Konto?{" "}
              <Link to={`/registrieren${location.search}`} className="text-primary font-medium hover:underline">
                Jetzt registrieren
              </Link>
            </p>
            <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
              ← Zurück zur Startseite
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
    </>
  );
};

export default Login;